import { FC, useState, useContext, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPinIcon,
  CalendarDaysIcon,
  ClockIcon,
  CurrencyEuroIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  StarIcon,
  PhoneIcon,
  CreditCardIcon,
  QrCodeIcon,
} from "@heroicons/react/24/outline";
import { AuthContext } from "context/appContext";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import {
  cancelReservation,
  fetchReservationByClient,
  fetchReservationByOwner,
  fetchReservationByOwnerAsClient,
  repayReservationFee,
} from "redux/features/reservations/reservationSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import AlertModalDelete from "components/AlertModal/AlertModalDelete";
import CommentModal from "./CommentModal";
import { socket } from "../../utils/socket";
import toast from "react-hot-toast";

export interface ModernClientReservationCardProps {
  className?: string;
  defaultOpen?: boolean;
  data: any;
  totalCount?: number;
}

const ModernClientReservationCard: FC<ModernClientReservationCardProps> = ({
  className = "",
  data,
  defaultOpen = false,
}) => {
  const dispatch = useAppDispatch();
  const reservationState = useAppSelector((state) => state.featureReservation);
  const authContext = useContext(AuthContext);
  const profilId = authContext?.user?.profil?.id;
  const profil_status = authContext?.user?.profil?.validation;

  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [canceled, setCanceled] = useState(false);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showCommentModal, setShowCommentModal] = useState(false);

  useEffect(() => {
    if (reservationState.sessionUrl) {
      window.location.href = reservationState.sessionUrl;
    }
  }, [reservationState.sessionUrl]);

  useEffect(() => {
    if (reservationState.status === "failed") {
      toast.error("🔔 Quelque chose ne fonctionne pas, veuillez réessayer!");
    }
  }, [reservationState.status]);

  function formatDateShort(dateString: any) {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handleConfirm = async () => {
    setShowModal(false);
    setLoading(true);
    await dispatch(cancelReservation({ reservation: data.id }));
    setCanceled(true);
    setLoading(false);

    if (profilId) {
      if (profil_status !== "en cours") {
        await dispatch(fetchReservationByOwner({ id: profilId, page: 1 }));
        await dispatch(fetchReservationByOwnerAsClient({ id: profilId, page: 1 }));
      }
      await dispatch(fetchReservationByClient({ id: profilId, page: 1 }));
    }

    if (data) {
      socket.emit("send-notification", {
        senderId: data.Client.userId,
        receiverId: data.Owner.userId,
        action: "cancel_reservation",
        reservationId: data.id,
        reservation: data,
      });
    }
  };

  const handleSubmit = async () => {
    dispatch(repayReservationFee(data.id));
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "En cours":
        return {
          color: "text-green-600 dark:text-green-400",
          bg: "bg-green-50 dark:bg-green-900/20",
          border: "border-green-200 dark:border-green-800",
          icon: "🟢",
        };
      case "En attente":
      case "Non complété":
        return {
          color: "text-amber-600 dark:text-amber-400",
          bg: "bg-amber-50 dark:bg-amber-900/20",
          border: "border-amber-200 dark:border-amber-800",
          icon: "🟡",
        };
      case "Annulée":
        return {
          color: "text-red-600 dark:text-red-400",
          bg: "bg-red-50 dark:bg-red-900/20",
          border: "border-red-200 dark:border-red-800",
          icon: "🔴",
        };
      case "Terminée":
        return {
          color: "text-blue-600 dark:text-blue-400",
          bg: "bg-blue-50 dark:bg-blue-900/20",
          border: "border-blue-200 dark:border-blue-800",
          icon: "✅",
        };
      default:
        return {
          color: "text-gray-600 dark:text-gray-400",
          bg: "bg-gray-50 dark:bg-gray-900/20",
          border: "border-gray-200 dark:border-gray-800",
          icon: "⚪",
        };
    }
  };

  const statusConfig = getStatusConfig(data?.status);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`group relative overflow-hidden bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 ${className}`}
      >
        {/* Header Section */}
        <div className="relative p-6 pb-4">
          {/* Status Badge */}
          <div className="absolute top-4 right-4 flex items-center space-x-2">
            <div
              className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color} ${statusConfig.border} border`}
            >
              <span className="mr-1">{statusConfig.icon}</span>
              {data?.status}
            </div>

            {/* Review Button for Completed Events */}
            {data.status === "Terminée" && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full text-xs font-medium transition-colors duration-200 flex items-center space-x-1"
                onClick={() => setShowCommentModal(true)}
              >
                <StarIcon className="w-3 h-3" />
                <span>Laisser un avis</span>
              </motion.button>
            )}
          </div>

          {/* Main Content */}
          <div className="flex items-start space-x-4 pr-20">
            {/* Location Image */}
            <div className="flex-shrink-0">
              <div className="relative overflow-hidden rounded-xl">
                <img
                  src={`${process.env.REACT_APP_CLOUD_FRONT}${data?.Location?.images[0]}`}
                  className="w-20 h-20 object-cover"
                  alt={data?.Location?.title}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>

            {/* Event Details */}
            <div className="flex-1 min-w-0">
              <a href={`/location/${data?.Location?.id}`} className="group/link">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover/link:text-blue-600 dark:group-hover/link:text-blue-400 transition-colors">
                  {data?.Location?.title}
                </h3>
              </a>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <CalendarDaysIcon className="w-4 h-4" />
                  <span>{formatDateShort(data?.selectedDate)}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <CurrencyEuroIcon className="w-4 h-4" />
                  <span className="font-semibold">{data?.price} €</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <ClockIcon className="w-4 h-4" />
                  <span>
                    {data?.startHour} - {data?.endHour}
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
                  <UserGroupIcon className="w-4 h-4" />
                  <span>{data?.visitor} personnes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Expand/Collapse Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className={`absolute bottom-4 right-4 w-8 h-8 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full flex items-center justify-center transition-all duration-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 ${
              isOpen ? "transform rotate-180" : ""
            }`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <ChevronDownIcon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          </motion.button>
        </div>

        {/* Expandable Details */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden border-t border-gray-100 dark:border-gray-800"
            >
              <div className="p-6 space-y-6 bg-gray-50/50 dark:bg-gray-800/50">
                {/* Owner & Contact Info */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <BuildingOfficeIcon className="w-5 h-5" />
                      <span>Propriétaire</span>
                    </h4>
                    <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {data?.Owner?.name ? `${data?.Owner?.name}` : data?.Owner?.companyName}
                      </p>
                      {(data.status === "En cours" || data.status === "Terminée") && data?.Owner?.phone && (
                        <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600 dark:text-gray-400">
                          <PhoneIcon className="w-4 h-4" />
                          <span>+33 {data?.Owner?.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {(data.status === "En cours" || data.status === "Terminée") && (
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                        <MapPinIcon className="w-5 h-5" />
                        <span>Adresse</span>
                      </h4>
                      <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <p className="text-gray-900 dark:text-white">
                          {[data?.Location?.address, data?.Location?.city].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Details */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <CreditCardIcon className="w-5 h-5" />
                    <span>Détails du paiement</span>
                  </h4>
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Sous-total</span>
                        <span className="font-medium">{data?.halfTotal} €</span>
                      </div>
                      {data?.extra && data?.extra !== 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Extra</span>
                          <span className="font-medium">{data?.extra} €</span>
                        </div>
                      )}
                      {data?.cleaning_costs && data?.cleaning_costs !== 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Frais de ménage</span>
                          <span className="font-medium">{data?.cleaning_costs} €</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Service</span>
                        <span className="font-medium">{data?.service} €</span>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
                        <div className="flex justify-between">
                          <span className="font-semibold text-gray-900 dark:text-white">Total</span>
                          <span className="font-bold text-lg text-blue-600 dark:text-blue-400">
                            {data?.price} €
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reservation Code */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <QrCodeIcon className="w-5 h-5" />
                    <span>Code de réservation</span>
                  </h4>
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                    <code className="text-lg font-mono font-bold text-blue-600 dark:text-blue-400">
                      {data?.reference}
                    </code>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {data?.action === "cancel" && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-medium transition-colors duration-200"
                      onClick={() => setShowModal(true)}
                      disabled={loading}
                    >
                      {loading ? "Annulation..." : "Annuler réservation"}
                    </motion.button>
                  )}

                  {data?.action === "complete" && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-medium transition-colors duration-200"
                      onClick={handleSubmit}
                    >
                      Terminer réservation
                    </motion.button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Modals */}
      <AlertModalDelete
        open={showModal}
        handleDelete={handleConfirm}
        handleClose={() => setShowModal(false)}
        message="Êtes-vous sûr(e) de vouloir annuler votre réservation ?"
        alertText="Annulation de Réservation"
      />

      {showCommentModal && (
        <CommentModal
          open={showCommentModal}
          handleClose={() => setShowCommentModal(false)}
          locationId={data?.Location?.id}
        />
      )}
    </>
  );
};

export default ModernClientReservationCard;
